import React, { useEffect, useState } from "react";
import { apiGet, apiPost, apiPut, apiDelete } from "../../utils/api";
import {
  Card, CardBody, PageHeader, Badge, Spinner
} from "../../components/ui";
import { FaEdit, FaTrash, FaPlus, FaTimes, FaCheck, FaCrown } from "react-icons/fa";
import { useModal } from "../../context/ModalContext";

const emptyForm = {
  key: "", name: "", description: "",
  prices: [
    { interval: "month", amount: 0, stripePriceId: "" },
    { interval: "year", amount: 0, stripePriceId: "" },
    { interval: "lifetime", amount: 0, stripePriceId: "" },
  ],
  features: [],
  isFree: false, badge: "", sortOrder: 0,
};

export default function ManagePlans() {
  const modal = useModal();
  const [plans, setPlans] = useState([]);
  const [features, setFeatures] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ ...emptyForm });
  const [editingId, setEditingId] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null);

  const showToast = (msg, type = "success") => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 4000);
  };

  const load = async () => {
    try {
      const [resPlans, resFeatures] = await Promise.all([
        apiGet("/api/plans"),
        apiGet("/api/features"),
      ]);
      setPlans(resPlans?.plans || []);
      setFeatures(resFeatures?.data || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const handlePriceChange = (interval, field, value) => {
    setForm((prev) => {
      const prices = [...prev.prices];
      const idx = prices.findIndex((p) => p.interval === interval);
      if (idx >= 0) {
        prices[idx] = { ...prices[idx], [field]: field === "amount" ? Number(value) : value };
      } else {
        prices.push({ interval, amount: field === "amount" ? Number(value) : 0, stripePriceId: field === "stripePriceId" ? value : "" });
      }
      return { ...prev, prices };
    });
  };

  const handleFeatureChange = (key, value, type) => {
    setForm((prev) => {
      const existing = prev.features.find((f) => f.key === key);
      const parsed = type === "bool" ? value === "true" : value;
      const newFeatures = existing
        ? prev.features.map((f) => (f.key === key ? { ...f, value: parsed } : f))
        : [...prev.features, { key, value: parsed }];
      return { ...prev, features: newFeatures };
    });
  };

  const openCreate = () => {
    setEditingId(null);
    setForm({ ...emptyForm, prices: [{ interval: "month", amount: 0, stripePriceId: "" }, { interval: "year", amount: 0, stripePriceId: "" }, { interval: "lifetime", amount: 0, stripePriceId: "" }] });
    setShowForm(true);
  };

  const openEdit = (plan) => {
    setEditingId(plan.id);
    setForm({
      ...plan,
      prices: plan.prices?.length ? [...plan.prices, ...(!plan.prices.find(p => p.interval === "lifetime") ? [{ interval: "lifetime", amount: 0, stripePriceId: "" }] : [])] : [{ interval: "month", amount: 0, stripePriceId: "" }, { interval: "year", amount: 0, stripePriceId: "" }, { interval: "lifetime", amount: 0, stripePriceId: "" }],
      isFree: plan.isFree || false,
      badge: plan.badge || "",
      sortOrder: plan.sortOrder || 0,
    });
    setShowForm(true);
  };

  const closeForm = () => {
    setShowForm(false);
    setEditingId(null);
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const res = editingId
        ? await apiPut(`/api/plans/${editingId}`, form)
        : await apiPost("/api/plans", form);

      if (res?.success) {
        showToast(editingId ? "Plan updated successfully" : "Plan created and synced with Stripe");
        closeForm();
        load();
      } else {
        showToast(res?.message || "Failed to save plan", "error");
      }
    } catch (err) {
      showToast(err?.message || "An error occurred", "error");
    } finally {
      setSaving(false);
    }
  };

  const deletePlan = async (plan) => {
    if (!(await modal.confirm(`Delete "${plan.name}"? This cannot be undone.`, { danger: true, confirmText: "Delete" }))) return;
    try {
      const res = await apiDelete(`/api/plans/${plan.id}`);
      if (res?.success) { showToast("Plan deleted"); load(); }
      else showToast("Failed to delete plan", "error");
    } catch { showToast("Failed to delete plan", "error"); }
  };

  if (loading) {
    return (
      <div>
        <PageHeader title="Manage Plans" subtitle="Create and manage subscription plans with Stripe" />
        <div className="flex items-center justify-center min-h-[300px]"><Spinner size="lg" /></div>
      </div>
    );
  }

  return (
    <div>
      <PageHeader title="Manage Plans" subtitle="Create and manage subscription plans with Stripe">
        <button className="btn-primary flex items-center gap-2" onClick={openCreate}>
          <FaPlus size={11} /> New Plan
        </button>
      </PageHeader>

      {/* Toast */}
      {toast && (
        <div className={`mb-4 px-4 py-3 rounded-lg text-sm font-medium flex items-center gap-2 ${
          toast.type === "error"
            ? "bg-red-500/10 text-red-500 border border-red-500/20"
            : "bg-green-500/10 text-green-500 border border-green-500/20"
        }`}>
          {toast.type === "error" ? "!" : <FaCheck size={12} />} {toast.msg}
        </div>
      )}

      {/* Plan Cards Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
        {plans.map((p) => {
          const monthly = p.prices?.find((pr) => pr.interval === "month");
          const yearly = p.prices?.find((pr) => pr.interval === "year");
          const lifetime = p.prices?.find((pr) => pr.interval === "lifetime");
          const hasStripe = p.prices?.some((pr) => pr.stripePriceId);

          return (
            <Card key={p.id} className="relative overflow-hidden">
              {p.badge && (
                <div className="absolute top-0 right-0">
                  <span className="badge-brand text-xs px-3 py-1 rounded-bl-lg rounded-tr-lg">{p.badge}</span>
                </div>
              )}
              <CardBody>
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="text-sm font-semibold text-ink-primary">{p.name}</h3>
                      {p.isFree && <Badge variant="success">Free</Badge>}
                    </div>
                    <p className="text-xs text-ink-muted">{p.key}</p>
                  </div>
                  <div className="flex items-center gap-1">
                    {hasStripe && (
                      <span className="w-5 h-5 rounded flex items-center justify-center bg-purple-500/10" title="Synced with Stripe">
                        <span className="text-purple-500 text-[10px] font-bold">S</span>
                      </span>
                    )}
                  </div>
                </div>

                {p.description && <p className="text-xs text-ink-muted mb-3">{p.description}</p>}

                {/* Pricing */}
                <div className="flex items-baseline gap-1 mb-3">
                  {p.isFree ? (
                    <span className="text-2xl font-bold text-ink-primary">Free</span>
                  ) : (
                    <>
                      <span className="text-2xl font-bold text-ink-primary">${monthly?.amount || 0}</span>
                      <span className="text-xs text-ink-muted">/mo</span>
                      {yearly?.amount > 0 && (
                        <span className="text-xs text-ink-muted ml-2">· ${yearly.amount}/yr</span>
                      )}
                      {lifetime?.amount > 0 && (
                        <span className="text-xs text-ink-muted ml-2">· ${lifetime.amount} lifetime</span>
                      )}
                    </>
                  )}
                </div>

                {/* Features summary */}
                {p.features?.length > 0 && (
                  <div className="mb-4 space-y-1">
                    {p.features.slice(0, 4).map((f, i) => (
                      <div key={i} className="flex items-center gap-2 text-xs text-ink-secondary">
                        <FaCheck size={9} className="text-brand flex-shrink-0" />
                        <span>{f.key}: {typeof f.value === "boolean" ? (f.value ? "Yes" : "No") : f.value}</span>
                      </div>
                    ))}
                    {p.features.length > 4 && (
                      <p className="text-xs text-ink-faint">+{p.features.length - 4} more</p>
                    )}
                  </div>
                )}

                {/* Stripe IDs */}
                {hasStripe && (
                  <div className="mb-3 p-2 rounded bg-surface-bg text-[10px] text-ink-faint font-mono space-y-0.5">
                    {monthly?.stripePriceId && <p>Monthly: {monthly.stripePriceId}</p>}
                    {yearly?.stripePriceId && <p>Yearly: {yearly.stripePriceId}</p>}
                    {lifetime?.stripePriceId && <p>Lifetime: {lifetime.stripePriceId}</p>}
                  </div>
                )}

                {/* Actions */}
                <div className="flex gap-2 pt-2 border-t border-surface-border">
                  <button className="btn-secondary btn-sm flex-1 flex items-center justify-center gap-1.5" onClick={() => openEdit(p)}>
                    <FaEdit size={11} /> Edit
                  </button>
                  <button className="btn-secondary btn-sm flex items-center gap-1.5 text-red-500 hover:!bg-red-950/20" onClick={() => deletePlan(p)}>
                    <FaTrash size={10} />
                  </button>
                </div>
              </CardBody>
            </Card>
          );
        })}

        {plans.length === 0 && (
          <div className="col-span-full text-center py-12 text-ink-muted text-sm">
            No plans yet. Click "New Plan" to create your first plan.
          </div>
        )}
      </div>

      {/* Create/Edit Modal */}
      {showForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center" onClick={closeForm}>
          <div className="absolute inset-0 bg-black/50" />
          <div className="relative w-full max-w-2xl mx-4 max-h-[90vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
            <Card className="flex flex-col max-h-[90vh]">
              {/* Fixed header */}
              <div className="flex items-center justify-between px-5 py-4 border-b border-surface-border flex-shrink-0">
                <h3 className="text-base font-semibold text-ink-primary flex items-center gap-2">
                  <FaCrown size={14} className="text-brand" />
                  {editingId ? "Edit Plan" : "Create New Plan"}
                </h3>
                <button className="text-ink-muted hover:text-ink-primary p-1" onClick={closeForm}><FaTimes size={14} /></button>
              </div>

              {/* Scrollable content */}
              <div className="flex-1 overflow-y-auto px-5 py-4">
                <form id="plan-form" onSubmit={submit} className="space-y-5">
                  {/* Basic Info */}
                  <div>
                    <h4 className="text-xs font-semibold text-ink-muted uppercase tracking-wider mb-3">Basic Information</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="input-group">
                        <label className="input-label">Plan Key</label>
                        <input className="input" placeholder="e.g. starter" value={form.key} onChange={(e) => setForm({ ...form, key: e.target.value })} required disabled={!!editingId} />
                        <p className="input-hint">Unique identifier (cannot change after creation)</p>
                      </div>
                      <div className="input-group">
                        <label className="input-label">Display Name</label>
                        <input className="input" placeholder="e.g. Starter Plan" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required />
                      </div>
                    </div>
                    <div className="input-group mt-3">
                      <label className="input-label">Description</label>
                      <textarea className="input min-h-[60px]" placeholder="Brief description of the plan" value={form.description || ""} onChange={(e) => setForm({ ...form, description: e.target.value })} />
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-3">
                      <div className="input-group">
                        <label className="input-label">Badge</label>
                        <input className="input" placeholder="e.g. Popular" value={form.badge || ""} onChange={(e) => setForm({ ...form, badge: e.target.value })} />
                      </div>
                      <div className="input-group">
                        <label className="input-label">Sort Order</label>
                        <input type="number" className="input" value={form.sortOrder || 0} onChange={(e) => setForm({ ...form, sortOrder: Number(e.target.value) })} />
                      </div>
                      <div className="input-group">
                        <label className="input-label">&nbsp;</label>
                        <label className="flex items-center gap-2 text-sm text-ink-secondary cursor-pointer h-[38px]">
                          <input type="checkbox" checked={form.isFree || false} onChange={(e) => setForm({ ...form, isFree: e.target.checked })} className="accent-[var(--color-brand)]" />
                          Free Plan
                        </label>
                      </div>
                    </div>
                  </div>

                  {/* Pricing */}
                  {!form.isFree && (
                    <div>
                      <h4 className="text-xs font-semibold text-ink-muted uppercase tracking-wider mb-3">
                        Pricing (USD)
                        <span className="text-[10px] font-normal normal-case ml-2 text-ink-faint">Stripe prices are auto-created on save</span>
                      </h4>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                        {["month", "year", "lifetime"].map((interval) => {
                          const price = form.prices.find((p) => p.interval === interval);
                          return (
                            <div key={interval} className="p-3 rounded-lg border border-surface-border">
                              <p className="text-xs font-medium text-ink-primary mb-2 capitalize">{interval === "month" ? "Monthly" : interval === "year" ? "Yearly" : "Lifetime"}</p>
                              <div className="input-group">
                                <label className="input-label">Amount ($)</label>
                                <input type="number" step="0.01" className="input" value={price?.amount || ""} onChange={(e) => handlePriceChange(interval, "amount", e.target.value)} />
                              </div>
                              {price?.stripePriceId && (
                                <p className="text-[10px] text-ink-faint font-mono mt-1 break-all">{price.stripePriceId}</p>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* Features grouped by module */}
                  {features.length > 0 && (() => {
                    const moduleGroups = {};
                    features.forEach((f) => {
                      const mod = f.module || "General";
                      if (!moduleGroups[mod]) moduleGroups[mod] = [];
                      moduleGroups[mod].push(f);
                    });
                    return (
                      <div>
                        <h4 className="text-xs font-semibold text-ink-muted uppercase tracking-wider mb-3">Feature Limits</h4>
                        <div className="space-y-3">
                          {Object.entries(moduleGroups).sort(([a], [b]) => a.localeCompare(b)).map(([mod, modFeatures]) => (
                            <div key={mod} className="p-3 rounded-lg border border-surface-border">
                              <p className="text-xs font-semibold text-ink-primary mb-3 flex items-center gap-2">
                                <span className="w-1.5 h-1.5 rounded-full bg-brand" />
                                {mod}
                              </p>
                              <div className="space-y-2">
                                {modFeatures.map((f) => {
                                  const existing = form.features.find((x) => x.key === f.key);
                                  return f.type === "bool" ? (
                                    <label key={f.id} className="flex items-center justify-between py-1.5 cursor-pointer group">
                                      <span className="text-xs text-ink-secondary group-hover:text-ink-primary transition-colors">{f.label}</span>
                                      <div className="relative">
                                        <input
                                          type="checkbox"
                                          className="sr-only peer"
                                          checked={!!existing?.value}
                                          onChange={(e) => handleFeatureChange(f.key, e.target.checked ? "true" : "false", "bool")}
                                        />
                                        <div className="w-9 h-5 bg-surface-border rounded-full peer-checked:bg-brand transition-colors" />
                                        <div className="absolute left-0.5 top-0.5 w-4 h-4 bg-white rounded-full shadow-sm peer-checked:translate-x-4 transition-transform" />
                                      </div>
                                    </label>
                                  ) : (
                                    <div key={f.id} className="flex items-center justify-between gap-4 py-1">
                                      <span className="text-xs text-ink-secondary">{f.label}</span>
                                      <input type="number" className="input w-28 text-center py-1.5 text-xs" placeholder="-1 = unlimited" value={existing?.value ?? ""} onChange={(e) => handleFeatureChange(f.key, e.target.value, "value")} />
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  })()}
                </form>
              </div>

              {/* Fixed footer */}
              <div className="flex gap-2 px-5 py-3 border-t border-surface-border flex-shrink-0">
                <button type="submit" form="plan-form" className="btn-primary flex items-center gap-2" disabled={saving}>
                  {saving ? <Spinner size="sm" /> : <FaCheck size={11} />}
                  {saving ? "Saving..." : editingId ? "Update Plan" : "Create Plan"}
                </button>
                <button type="button" className="btn-secondary" onClick={closeForm}>Cancel</button>
              </div>
            </Card>
          </div>
        </div>
      )}
    </div>
  );
}
